import React, { useEffect, useState } from 'react';
import { preferencesApi } from '../api/client';
import type { SitePreferences } from '../api/client';
import './SitePreferences.css';

const SitePreferencesPage: React.FC = () => {
  const [preferences, setPreferences] = useState<SitePreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<keyof Omit<SitePreferences, 'lastUpdated' | 'version'>>('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<Partial<SitePreferences>>({});

  const tabs: { key: keyof Omit<SitePreferences, 'lastUpdated' | 'version'>; label: string; icon: string }[] = [
    { key: 'general', label: 'General', icon: '⚙️' },
    { key: 'appearance', label: 'Appearance', icon: '🎨' },
    { key: 'security', label: 'Security', icon: '🔒' },
    { key: 'notifications', label: 'Notifications', icon: '🔔' },
    { key: 'content', label: 'Content', icon: '📝' },
    { key: 'features', label: 'Features', icon: '🔧' },
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await preferencesApi.getSitePreferences();
      setPreferences(response.data);
      setFormData(response.data);
      setHasChanges(false);
    } catch (err) {
      setError('Failed to load site preferences');
      console.error('Site preferences error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;
    
    try {
      const response = await preferencesApi.updateSitePreferences(formData);
      setPreferences(response.data);
      setFormData(response.data);
      setHasChanges(false);
      setError(null);
    } catch (err) {
      setError('Failed to update site preferences');
      console.error('Update site preferences error:', err);
    }
  };

  const handleDiscard = () => {
    setFormData(preferences || {});
    setHasChanges(false);
  };

  const handleSectionChange = (section: keyof SitePreferences, field: string, value: any) => {
    setFormData(prev => {
      const currentSection = prev[section as keyof typeof prev];
      const sectionData = typeof currentSection === 'object' && currentSection !== null ? currentSection : {};
      
      return {
        ...prev,
        [section]: {
          ...sectionData,
          [field]: value
        }
      };
    });
    setHasChanges(true);
  };

  const renderGeneralTab = () => {
    const section = formData.general || {} as SitePreferences['general'];
    return (
      <div className="preferences-section">
        <h3>⚙️ General Settings</h3>
        <p className="section-description">Basic site configuration and information</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Site Information</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Site Name</label>
                <input
                  type="text"
                  value={section.siteName || ''}
                  onChange={(e) => handleSectionChange('general', 'siteName', e.target.value)}
                  className="form-input"
                  placeholder="Enter site name"
                />
              </div>
              <div className="preference-item">
                <label>Site Description</label>
                <textarea
                  value={section.siteDescription || ''}
                  onChange={(e) => handleSectionChange('general', 'siteDescription', e.target.value)}
                  className="form-textarea"
                  placeholder="Enter site description"
                  rows={3}
                />
              </div>
              <div className="preference-item">
                <label>Site URL</label>
                <input
                  type="url"
                  value={section.siteUrl || ''}
                  onChange={(e) => handleSectionChange('general', 'siteUrl', e.target.value)}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Contact Information</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Admin Email</label>
                <input
                  type="email"
                  value={section.adminEmail || ''}
                  onChange={(e) => handleSectionChange('general', 'adminEmail', e.target.value)}
                  className="form-input"
                  placeholder="admin@example.com"
                />
              </div>
              <div className="preference-item">
                <label>Support Email</label>
                <input
                  type="email"
                  value={section.supportEmail || ''}
                  onChange={(e) => handleSectionChange('general', 'supportEmail', e.target.value)}
                  className="form-input"
                  placeholder="support@example.com"
                />
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Registration & Access</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.allowRegistration || false}
                    onChange={(e) => handleSectionChange('general', 'allowRegistration', e.target.checked)}
                  />
                  <span>Allow new user registration</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.requireEmailVerification || false}
                    onChange={(e) => handleSectionChange('general', 'requireEmailVerification', e.target.checked)}
                  />
                  <span>Require email verification</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.maintenanceMode || false}
                    onChange={(e) => handleSectionChange('general', 'maintenanceMode', e.target.checked)}
                  />
                  <span>Maintenance mode</span>
                </label>
              </div>
              {section.maintenanceMode && (
                <div className="preference-item">
                  <label>Maintenance Message</label>
                  <textarea
                    value={section.maintenanceMessage || ''}
                    onChange={(e) => handleSectionChange('general', 'maintenanceMessage', e.target.value)}
                    className="form-textarea"
                    placeholder="Site is under maintenance..."
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSecurityTab = () => {
    const section = formData.security || {} as SitePreferences['security'];
    return (
      <div className="preferences-section">
        <h3>🔒 Security Settings</h3>
        <p className="section-description">Configure security policies and authentication settings</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Password Policy</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Minimum Password Length</label>
                <input
                  type="number"
                  min="6"
                  max="50"
                  value={section.passwordMinLength || 8}
                  onChange={(e) => handleSectionChange('security', 'passwordMinLength', parseInt(e.target.value))}
                  className="form-input"
                />
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.requireStrongPasswords || false}
                    onChange={(e) => handleSectionChange('security', 'requireStrongPasswords', e.target.checked)}
                  />
                  <span>Require strong passwords</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.requireTwoFactor || false}
                    onChange={(e) => handleSectionChange('security', 'requireTwoFactor', e.target.checked)}
                  />
                  <span>Require two-factor authentication</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Session & Access Control</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label>Session Timeout (seconds)</label>
                <input
                  type="number"
                  min="300"
                  max="86400"
                  value={section.sessionTimeout || 86400}
                  onChange={(e) => handleSectionChange('security', 'sessionTimeout', parseInt(e.target.value))}
                  className="form-input"
                />
              </div>
              <div className="preference-item">
                <label>Max Login Attempts</label>
                <input
                  type="number"
                  min="3"
                  max="20"
                  value={section.maxLoginAttempts || 5}
                  onChange={(e) => handleSectionChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                  className="form-input"
                />
              </div>
              <div className="preference-item">
                <label>Lockout Duration (seconds)</label>
                <input
                  type="number"
                  min="60"
                  max="3600"
                  value={section.lockoutDuration || 900}
                  onChange={(e) => handleSectionChange('security', 'lockoutDuration', parseInt(e.target.value))}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Protection</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableBruteForceProtection || false}
                    onChange={(e) => handleSectionChange('security', 'enableBruteForceProtection', e.target.checked)}
                  />
                  <span>Enable brute force protection</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableRateLimiting || false}
                    onChange={(e) => handleSectionChange('security', 'enableRateLimiting', e.target.checked)}
                  />
                  <span>Enable rate limiting</span>
                </label>
              </div>
              {section.enableRateLimiting && (
                <>
                  <div className="preference-item">
                    <label>Rate Limit Window (seconds)</label>
                    <input
                      type="number"
                      min="60"
                      max="3600"
                      value={section.rateLimitWindow || 900}
                      onChange={(e) => handleSectionChange('security', 'rateLimitWindow', parseInt(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div className="preference-item">
                    <label>Max Requests per Window</label>
                    <input
                      type="number"
                      min="10"
                      max="1000"
                      value={section.rateLimitMaxRequests || 100}
                      onChange={(e) => handleSectionChange('security', 'rateLimitMaxRequests', parseInt(e.target.value))}
                      className="form-input"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFeaturesTab = () => {
    const section = formData.features || {} as SitePreferences['features'];
    return (
      <div className="preferences-section">
        <h3>🔧 Feature Settings</h3>
        <p className="section-description">Enable or disable site features and modules</p>
        
        <div className="preference-groups">
          <div className="preference-group">
            <h4>Core Features</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableUserProfiles || false}
                    onChange={(e) => handleSectionChange('features', 'enableUserProfiles', e.target.checked)}
                  />
                  <span>User Profiles</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableBlogs || false}
                    onChange={(e) => handleSectionChange('features', 'enableBlogs', e.target.checked)}
                  />
                  <span>Blogs</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableSearch || false}
                    onChange={(e) => handleSectionChange('features', 'enableSearch', e.target.checked)}
                  />
                  <span>Search</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Personal Management</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableProjects || false}
                    onChange={(e) => handleSectionChange('features', 'enableProjects', e.target.checked)}
                  />
                  <span>Projects</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableGoals || false}
                    onChange={(e) => handleSectionChange('features', 'enableGoals', e.target.checked)}
                  />
                  <span>Goals</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableHabits || false}
                    onChange={(e) => handleSectionChange('features', 'enableHabits', e.target.checked)}
                  />
                  <span>Habits</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableMetrics || false}
                    onChange={(e) => handleSectionChange('features', 'enableMetrics', e.target.checked)}
                  />
                  <span>Health Metrics</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Data Management</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableContacts || false}
                    onChange={(e) => handleSectionChange('features', 'enableContacts', e.target.checked)}
                  />
                  <span>Contacts</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableVehicles || false}
                    onChange={(e) => handleSectionChange('features', 'enableVehicles', e.target.checked)}
                  />
                  <span>Vehicles</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableBookmarks || false}
                    onChange={(e) => handleSectionChange('features', 'enableBookmarks', e.target.checked)}
                  />
                  <span>Bookmarks</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableCampaigns || false}
                    onChange={(e) => handleSectionChange('features', 'enableCampaigns', e.target.checked)}
                  />
                  <span>Campaigns</span>
                </label>
              </div>
            </div>
          </div>

          <div className="preference-group">
            <h4>Organization</h4>
            <div className="preference-items">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableTags || false}
                    onChange={(e) => handleSectionChange('features', 'enableTags', e.target.checked)}
                  />
                  <span>Tags System</span>
                </label>
              </div>
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={section.enableCategories || false}
                    onChange={(e) => handleSectionChange('features', 'enableCategories', e.target.checked)}
                  />
                  <span>Categories System</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentTab = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralTab();
      case 'security':
        return renderSecurityTab();
      case 'features':
        return renderFeaturesTab();
      default:
        return (
          <div className="preferences-section">
            <h3>Coming Soon</h3>
            <p>This preferences section is under development.</p>
          </div>
        );
    }
  };

  if (loading) {
    return <div className="site-preferences-page"><div className="loading">Loading site preferences...</div></div>;
  }

  if (error && !preferences) {
    return <div className="site-preferences-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="site-preferences-page">
      <header className="page-header">
        <h1>🛠️ Site Administration</h1>
        <div className="header-actions">
          {hasChanges && (
            <div className="edit-actions">
              <button className="btn btn-success" onClick={handleSave}>
                Save Changes
              </button>
              <button className="btn btn-secondary" onClick={handleDiscard}>
                Discard
              </button>
            </div>
          )}
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="site-info-banner">
        <div className="site-info">
          <h2>{preferences?.general?.siteName || 'SelfServe'}</h2>
          <p>Last updated: {preferences?.lastUpdated ? new Date(preferences.lastUpdated).toLocaleString() : 'Never'}</p>
          <p>Version: {preferences?.version || '1.0.0'}</p>
        </div>
      </div>

      <div className="preferences-content">
        <nav className="preferences-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="preferences-panel">
          {renderCurrentTab()}
        </div>
      </div>
    </div>
  );
};

export default SitePreferencesPage;